// ============================================================
// ClaimCard.tsx — the CLAIM card in Case control (B2; audit fix batch 1).
// Claim type (required — can be changed, not cleared), route, track,
// complexity band (1–4), fixed recoverable costs (a Yes is SUGGESTED for an
// accident on/after 1 Oct 2023 — not for disease claims; the user confirms),
// date of knowledge, date of death (fatal claims, or once one is saved), and
// "Set limitation date myself" (date + REASON).
// (B2b) Only the limitation inputs that matter for the type / route show
// (utils/caseClaim claimExtraFields): holiday type + arrival date + foreign
// months; abuse type (+ the "no time limit" confirm block — its own action,
// components/sections/AbuseNoLimitBlock); CICA police report date; military
// overseas operation (its longstop is a RED block on the own date); disease
// first symptoms / diagnosis. None of these dates can be in the future.
//
// SAVE MODEL — an explicit-save card (unlike the write-through cards around
// it): the pencil opens a DRAFT; nothing is sent until Save, and Save sends
// every changed field together in ONE request (utils/caseClaim
// claimSavePayload). Cancel throws the draft away. While ANY field has an
// error (utils/caseClaim claimDraftErrors — or a date still being typed, or
// the server's refusal) Save is disabled with "Fix the highlighted fields to
// save." — nothing is ever saved partly or dropped quietly. The own date and
// its reason always travel as a pair, and the server makes the date the
// case's REAL Limitation deadline (its note = the full reason). Amber
// warnings (claimDraftWarnings) never block.
// Dates: the own limitation date may be in the future (allowFuture); date of
// knowledge / death take any real date too, so a future one shows "Can't be
// in the future" here instead of the widget silently refusing it.
// After a save: the store takes the server's values without re-saving them
// (useAutoSave applyRemoteCaseEdit) and the case's deadlines are reloaded.
// ============================================================
import { refusalText } from '../../utils/serverError'
import ActionBox from '../shared/ActionBox'
import React, { useEffect, useState } from 'react'
import type { CaseClaim } from '../../types/case.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import FormField from '../shared/FormField'
import DateInput from '../shared/DateInput'
import FieldError from '../shared/FieldError'
import { inpCls, SELECT_BG, FGRID, IcEdit, IcCheck } from '../clientInfo/clientInfoRegistry'
import { CARD, CARD_EDITING, BTN_SEC, BTN_PRI, SM } from '../clientInfo/ClientInfoSections'
import { KIcon, Kv, Empty, Muted, DL, HINT, SegButtons, REVEALED } from '../case/sections/sectionKit'
import { fmtDay, ymdOf } from '../case/caseUi'
import { limitationRowOf } from '../case/caseFacts'
import {
  ABUSE_TYPES, ABUSE_TYPE_LABELS, CLAIM_ROUTE_LABELS, CLAIM_TRACKS, CLAIM_TRACK_LABELS, CLAIM_TYPES, CLAIM_TYPE_LABELS, COMPLEXITY_BANDS,
  HOLIDAY_REGIMES, HOLIDAY_REGIME_LABELS,
  abuseTypeLabel, claimFieldApplies, claimRouteLabel, routeOnTypeChange, routesFor, routeAllowedFor, claimTrackLabel, claimTypeLabel, complexityBandLabel, holidayRegimeLabel,
  type AbuseType, type ClaimRoute, type ClaimTrack, type ClaimType, type HolidayRegime,
} from '../../utils/claimTypes'
import {
  canConfirmNoTimeLimit, claimDraftErrors, clearLine, FRC_CLIN_NEG_HINT, FRC_NOT_YET, claimDraftWarnings, NO_CLAIM_WARNINGS, type ClaimDraftWarnings, claimExtraFields, claimFactsOf, claimOfRaw, claimSavePayload, draftFromClaim, frcSuggestionFor,
  FOREIGN_MONTHS_MAX, FOREIGN_MONTHS_MIN, FRC_DISEASE_HINT, REASON_MAX, showDateOfDeath, type ClaimDraft,
} from '../../utils/caseClaim'
import { abuseConfirmOffered, estimatedLimitation, militaryLongstop, noTimeLimitApplies } from '../../utils/limitationRules'
import AbuseNoLimitBlock from './AbuseNoLimitBlock'
import { caseDobOf } from '../../utils/caseDob'
import { londonToday, textDateToIso } from '../../utils/civilDate'
import { applyRemoteCaseEdit, flushAutoSave } from '../../hooks/useAutoSave'
import { getCaseBaseUpdatedAt, setCaseBaseUpdatedAt, setDeadlineBaseUpdatedAt, setDeadlineBaseValues } from '../../hooks/saveBaseVersion'
import { normaliseServerDates, serverChangedTouchedColumns } from '../../hooks/reconcile409'
import { refusedField } from '../../hooks/fieldRefusals'
import { fetchCaseById, listCaseDeadlinesApi, updateCaseApi } from '../../api/casevoApi'
import { useFieldFocusRequest } from '../../utils/fieldFocusRequest'
import { applyClaimNow, useClaimLive } from '../../hooks/claimLive'
import type { RawCase } from '../../types/case.types'

const DATE_FIELD = 'h-[38px] !rounded-[9px] !bg-casevo-input focus:!bg-casevo-surface border-casevo-border'
// A date with an error is outlined like the other highlighted fields.
const dateCls = (bad: boolean): string => (bad ? `${DATE_FIELD} !border-casevo-danger` : DATE_FIELD)
const SEL = (bad: boolean): string => `${inpCls(bad)} ${SELECT_BG}`
const WARN = 'text-xs text-casevo-warn mt-1'
export const CLAIM_TYPE_DATA_FIELD = 'automatic.claim-type'
export const EMPTY_CASE_CLAIM: CaseClaim = {
  claimType: null, claimRoute: null, track: null, complexityBand: null, frcRegime: null,
  dateOfKnowledge: null, dateOfDeath: null, limitationOverrideDate: null, limitationOverrideReason: '',
  holidayRegime: null, travelArrivalDate: null, foreignLimitationMonths: null, abuseType: null, policeFirstReportedAt: null,
  overseasOperation: null, firstSymptomsDate: null, diagnosisDate: null, abuseNoLimitConfirmedAt: null, abuseNoLimitConfirmedByUserId: null,
}
export const FIX_TO_SAVE = 'Fix the highlighted fields to save.'
export const SAVE_FAILED = 'Couldn’t save. Check your connection and try again — nothing was changed.'
export const SAVE_STALE = 'Someone else changed the claim details just now. Cancel to see their version, then make your change again.'

export const ownDateDiffers = (own: string, deadline: string): string =>
  `Your own date ${fmtDay(own)} differs from the Limitation deadline ${fmtDay(deadline)}.`

// Send the claim fields with the case's concurrency base. A 409 caused by
// someone else saving OTHER fields is retried once on the fresh base; if they
// changed one of these claim fields → stale (the person decides).
class StaleClaimError extends Error {}
const putClaim = async (caseId: string, body: Record<string, unknown>, saved: CaseClaim, draft: ClaimDraft): Promise<RawCase> => {
  const send = (): Promise<RawCase> => {
    const base = getCaseBaseUpdatedAt(caseId)
    return updateCaseApi(caseId, base !== null ? { ...body, baseUpdatedAt: base } : body)
  }
  try {
    return await send()
  } catch (err) {
    if ((err as { response?: { status?: number } })?.response?.status !== 409) throw err
    const server = await fetchCaseById(caseId)
    const keys = Object.keys(body)
    const clash = serverChangedTouchedColumns(
      keys, saved as unknown as Record<string, unknown>,
      normaliseServerDates(server as unknown as Record<string, unknown>), draft as unknown as Record<string, unknown>,
    )
    if (clash) throw new StaleClaimError()
    setCaseBaseUpdatedAt(caseId, server.updatedAt)
    return send()
  }
}

const reloadDeadlines = async (caseId: string): Promise<void> => {
  try {
    const list = await listCaseDeadlinesApi(caseId)
    list.forEach((dl) => { setDeadlineBaseUpdatedAt(dl.id, dl.updatedAt); setDeadlineBaseValues(dl.id, dl as unknown as Record<string, unknown>) })
    useCasevoStore.getState().setCaseDeadlines(caseId, list)
  } catch { /* the next case open reloads them */ }
}

const ClaimCard: React.FC = () => {
  const currentCase = useCasevoStore((s) => s.currentCase)
  const authUser = useCasevoStore((s) => s.authUser)
  const caseId = currentCase?.id
  const caseDeadlines = useCasevoStore((s) => (caseId ? s.caseDeadlines[caseId] : undefined))
  const [draft, setDraft] = useState<ClaimDraft | null>(null)
  // Date boxes holding a half-typed date (DateInput onValidityChange).
  const [typing, setTyping] = useState<Record<string, boolean>>({})
  const [serverErr, setServerErr] = useState<Partial<Record<keyof CaseClaim, string>>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  // (B2b fix 2) The route follows the claim type's suggestion until the user
  // picks a route by hand in this edit; a brief highlight when it changes.
  const [routeTouched, setRouteTouched] = useState(false)
  const [routeFlash, setRouteFlash] = useState(false)
  useEffect(() => {
    if (!routeFlash) return
    const t = setTimeout(() => setRouteFlash(false), 1600)
    return () => clearTimeout(t)
  }, [routeFlash])
  const saved = currentCase?.claim ?? EMPTY_CASE_CLAIM
  const editing = draft !== null
  const idPrefix = `cc-${caseId ?? 'none'}-claim`

  // Live (hooks/claimLive): someone else's save while this draft is open waits.
  const pendingAny = useClaimLive((s) => s.pending)
  const pending = pendingAny && pendingAny.caseId === caseId ? pendingAny : null
  useEffect(() => () => { useClaimLive.getState().setDraftOpen(false) }, [])

  const open = (): void => {
    useClaimLive.getState().setDraftOpen(true)
    useClaimLive.getState().setPending(null)
    setDraft(draftFromClaim(useCasevoStore.getState().currentCase?.claim ?? EMPTY_CASE_CLAIM))
    setTyping({}); setServerErr({}); setSaveError(null); setRouteTouched(false); setRouteFlash(false)
  }
  const closeDraft = (): void => { useClaimLive.getState().setDraftOpen(false); setDraft(null); setTyping({}); setServerErr({}); setSaveError(null) }
  // Cancel: the draft goes, and anyone else's change that waited is taken.
  const cancel = (): void => {
    const waiting = useClaimLive.getState().pending
    closeDraft()
    if (waiting) { useClaimLive.getState().setPending(null); applyClaimNow(waiting) }
  }
  // "Reload": take their change and restart the draft from it.
  const reload = (): void => {
    if (!pending) return
    applyClaimNow(pending)
    useClaimLive.getState().setPending(null)
    setDraft(draftFromClaim(pending.claim)); setTyping({}); setServerErr({}); setSaveError(null)
  }
  // "Take me to the claim type" (the required-fields chip / the hero link).
  useFieldFocusRequest(
    (field) => (field === 'claimType' ? 'claim' : null),
    () => { if (!editing) open() },
    () => editing,
    () => `[data-focus-card="${idPrefix}"]`,
  )
  if (!currentCase) return null

  const today = londonToday()
  const accidentIso = textDateToIso(currentCase.accidentDetails.accidentDate)
  const dobInfo = caseDobOf(currentCase.clientDetails?.dateOfBirth, currentCase.client?.dateOfBirth)
  const dobIso = dobInfo.dob
  // (Audit fix 1) Both dates of birth when they differ → the earlier estimate.
  const limDob = dobInfo.limitationDob
  const deadlineYmd = ymdOf(limitationRowOf(caseDeadlines)?.dueDate)
  const setBy = currentCase.limitationOverrideSetBy

  const set = (changes: Partial<ClaimDraft>): void => {
    setDraft((d) => (d ? { ...d, ...changes } : d))
    // A refused field being edited again drops the server's message.
    setServerErr((e) => {
      const next = { ...e }
      for (const k of Object.keys(changes)) delete next[k as keyof CaseClaim]
      return next
    })
    setSaveError(null)
  }
  const typingFor = (k: string) => (ok: boolean): void => setTyping((t) => ({ ...t, [k]: !ok }))

  const errors = draft ? { ...claimDraftErrors(draft, { accidentIso, today, saved: { claimType: saved.claimType ?? null, claimRoute: saved.claimRoute ?? null } }), ...serverErr } : {}
  const blocked = Object.keys(errors).length > 0 || Object.values(typing).some(Boolean)
  // The estimate without an own date (for "later than Casevo's estimate").
  const estimateYmd = draft
    ? estimatedLimitation(accidentIso, limDob, { ...claimFactsOf(draft, currentCase.protectedParty), overrideDate: null })?.ymd ?? null
    : null
  const warnings = draft ? claimDraftWarnings(draft, { accidentIso, dobIso, today, estimateYmd }) : NO_CLAIM_WARNINGS

  const save = async (): Promise<void> => {
    if (!draft || blocked || saving) return
    setSaving(true)
    setSaveError(null)
    try {
      // Anything else still pending on the case goes first (current base).
      await flushAutoSave()
      const body = claimSavePayload(saved, draft)
      if (Object.keys(body).length > 0) {
        const result = await putClaim(currentCase.id, body, saved, draft)
        const next = claimOfRaw(result)
        const me = `${authUser?.firstName ?? ''} ${authUser?.lastName ?? ''}`.trim() || authUser?.email || 'You'
        const nextSetBy = !next.limitationOverrideDate ? null
          : next.limitationOverrideDate !== saved.limitationOverrideDate ? { name: me, at: new Date().toISOString() }
          : currentCase.limitationOverrideSetBy ?? null
        // Into the store WITHOUT a re-save (advances the autosave baselines).
        applyRemoteCaseEdit({ claim: next, limitationOverrideSetBy: nextSetBy }, result.updatedAt)
        if (next.limitationOverrideDate || saved.limitationOverrideDate) await reloadDeadlines(currentCase.id)
      }
      // Ours is now the latest — anything that waited is older.
      useClaimLive.getState().setPending(null)
      closeDraft()
    } catch (err) {
      const refused = refusedField(err)
      if (refused) setServerErr({ [refused.field]: refused.message })
      else setSaveError(err instanceof StaleClaimError ? SAVE_STALE : refusalText(err, SAVE_FAILED))
    } finally {
      setSaving(false)
    }
  }

  // ── view ──
  const extraSaved = claimExtraFields(saved)
  const yesNo = (v: boolean | null): React.ReactNode => (v === true ? <span>Yes</span> : v === false ? <span>No</span> : <Muted>Not answered</Muted>)
  const dayOrNot = (v: string | null): React.ReactNode => (v ? <span>{fmtDay(v)}</span> : <Muted>Not recorded</Muted>)
  // The "no time limit" block: offered on the SAVED values, or while confirmed.
  const abuseOffered = abuseConfirmOffered(accidentIso, limDob, claimFactsOf(saved))
  const showAbuseBlock = !!saved.abuseNoLimitConfirmedAt || abuseOffered
  // (B2b follow-up) checked NOW: a stored confirmation that no longer fits
  // counts as unconfirmed; only the fee earner or an admin may confirm.
  const abuseApplies = noTimeLimitApplies(accidentIso, limDob, claimFactsOf(saved))
  const mayConfirm = !!authUser && canConfirmNoTimeLimit({ userId: authUser.id, role: authUser.role }, currentCase)
  const suggestFrcSaved = frcSuggestionFor(saved.claimType, accidentIso)
  const frcText = saved.frcRegime === true ? 'Yes' : saved.frcRegime === false ? 'No' : null
  const view = (
    <dl className={`${DL} md:grid-cols-3`}>
      <Kv label="Claim type">{saved.claimType ? <span>{claimTypeLabel(saved.claimType)}</span> : <Empty onAdd={open} />}</Kv>
      <Kv label="Route">{saved.claimRoute ? <span>{claimRouteLabel(saved.claimRoute)}</span> : <Muted>Not set</Muted>}</Kv>
      {/* (B2b fix 2) CICA isn't a court claim: no track, band or fixed costs. */}
      {claimFieldApplies('track', saved) && <Kv label="Track">{saved.track ? <span>{claimTrackLabel(saved.track)}</span> : <Muted>Not set</Muted>}</Kv>}
      {claimFieldApplies('complexityBand', saved) && <Kv label="Complexity band">{saved.complexityBand ? <span>{complexityBandLabel(saved.complexityBand)}</span> : <Muted>Not set</Muted>}</Kv>}
      {saved.claimType === 'CLINICAL_NEGLIGENCE' ? (
        <Kv label="Fixed recoverable costs"><Muted>{FRC_NOT_YET}</Muted><span className="block text-[12.5px] text-casevo-text-soft">{FRC_CLIN_NEG_HINT}</span></Kv>
      ) : claimFieldApplies('frcRegime', saved) && (
        <Kv label="Fixed recoverable costs">
          {frcText ? <span>{frcText}</span> : <Muted>Not confirmed{suggestFrcSaved !== null ? ` · suggested ${suggestFrcSaved ? 'Yes' : 'No'}` : ''}</Muted>}
          {!frcText && saved.claimType === 'EL_DISEASE' && <span className="block text-[12.5px] text-casevo-text-soft">{FRC_DISEASE_HINT}</span>}
        </Kv>
      )}
      <Kv label="Date of knowledge">{saved.dateOfKnowledge ? <span>{fmtDay(saved.dateOfKnowledge)}</span> : <Muted>Not recorded</Muted>}</Kv>
      {showDateOfDeath(saved) && <Kv label="Date of death">{saved.dateOfDeath ? <span>{fmtDay(saved.dateOfDeath)}</span> : <Muted>Not recorded</Muted>}</Kv>}
      {extraSaved.holidayRegime && <Kv label="Type of holiday claim">{saved.holidayRegime ? <span>{holidayRegimeLabel(saved.holidayRegime)}</span> : <Muted>Not chosen</Muted>}</Kv>}
      {extraSaved.travelArrivalDate && <Kv label="Arrival date">{dayOrNot(saved.travelArrivalDate)}</Kv>}
      {extraSaved.foreignLimitationMonths && <Kv label="Foreign limitation period">{saved.foreignLimitationMonths != null ? <span>{saved.foreignLimitationMonths} month{saved.foreignLimitationMonths === 1 ? '' : 's'}</span> : <Muted>Not recorded</Muted>}</Kv>}
      {extraSaved.abuseType && <Kv label="Type of abuse">{saved.abuseType ? <span>{abuseTypeLabel(saved.abuseType)}</span> : <Muted>Not chosen</Muted>}</Kv>}
      {extraSaved.policeFirstReportedAt && <Kv label="First reported to police">{dayOrNot(saved.policeFirstReportedAt)}</Kv>}
      {extraSaved.overseasOperation && <Kv label="Overseas operation">{yesNo(saved.overseasOperation)}</Kv>}
      {extraSaved.diseaseDates && <Kv label="First symptoms">{dayOrNot(saved.firstSymptomsDate)}</Kv>}
      {extraSaved.diseaseDates && <Kv label="Diagnosis">{dayOrNot(saved.diagnosisDate)}</Kv>}
      <Kv label="Own date" full>
        {saved.limitationOverrideDate
          ? <span>{fmtDay(saved.limitationOverrideDate)}<span className="block text-[12.5px] text-casevo-text-soft">{setBy ? `Own date set by ${setBy.name}: ` : 'Own date: '}{saved.limitationOverrideReason}</span>
            {deadlineYmd && deadlineYmd !== saved.limitationOverrideDate && (
              <span className="block text-[12.5px] text-casevo-warn" role="status">{ownDateDiffers(saved.limitationOverrideDate, deadlineYmd)}</span>
            )}</span>
          : <Muted>Not set · the estimate is used</Muted>}
      </Kv>
    </dl>
  )

  // ── edit (the draft) ──
  const d = draft
  const suggestFrc = d ? frcSuggestionFor(d.claimType, accidentIso) : null
  const extra = d ? claimExtraFields(d) : null
  const cap = d ? militaryLongstop(accidentIso, claimFactsOf(d)) : null
  // A plain date box for the new (never-future) dates.
  const dateField = (k: 'travelArrivalDate' | 'policeFirstReportedAt' | 'firstSymptomsDate' | 'diagnosisDate', label: string, hint?: string): React.ReactNode => d && (
    <FormField variant="info" label={label}>
      <DateInput value={d[k]} allowFuture ariaLabel={label} fieldClassName={dateCls(!!errors[k])}
        onChange={(v) => set({ [k]: v || null } as Partial<ClaimDraft>)} onValidityChange={typingFor(k)} />
      <FieldError message={errors[k]} />
      {!errors[k] && (warnings[k as keyof ClaimDraftWarnings] ?? []).map((w) => <p key={w} className={WARN} role="status">{w}</p>)}
      {hint && <p className={HINT}>{hint}</p>}
    </FormField>
  )
  const edit = d && (
    <div className={`${FGRID} md:grid-cols-3`}>
      <div data-field={CLAIM_TYPE_DATA_FIELD}>
        <FormField variant="info" label="Claim type" required>
          <select aria-label="Claim type" aria-invalid={errors.claimType ? true : undefined} className={SEL(!!errors.claimType)} value={d.claimType ?? ''}
            onChange={(e) => {
              if (!e.target.value) return
              const t = e.target.value as ClaimType
              // (B3b) The route follows the type's suggestion only while it's
              // empty or still the previous type's suggestion; a route chosen by
              // hand (now or in an earlier edit) is kept — unless the new type
              // can't have it (MIB). utils/claimTypes routeOnTypeChange.
              const route = routeOnTypeChange(d.claimType, d.claimRoute, t, routeTouched)
              set({ claimType: t, claimRoute: route })
              if (route !== d.claimRoute) setRouteFlash(true)
            }}>
            {!d.claimType && <option value="">Choose…</option>}
            {CLAIM_TYPES.map((t) => <option key={t} value={t}>{CLAIM_TYPE_LABELS[t]}</option>)}
          </select>
          <FieldError message={errors.claimType} />
        </FormField>
      </div>
      <FormField variant="info" label="Route">
        <select aria-label="Route" className={`${SEL(!!errors.claimRoute)} transition-[box-shadow,background-color] duration-500 ${routeFlash ? '!bg-casevo-mint ring-2 ring-casevo-brand/40' : ''}`} value={d.claimRoute ?? ''}
          onChange={(e) => { setRouteTouched(true); setRouteFlash(false); set({ claimRoute: (e.target.value || null) as ClaimRoute | null }) }}>
          <option value="">Not set</option>
          {routesFor(d.claimType).map((r) => <option key={r} value={r}>{CLAIM_ROUTE_LABELS[r]}</option>)}
          {/* An older case saved with a route its type can't have: shown so it can be changed. */}
          {d.claimRoute && !routeAllowedFor(d.claimRoute, d.claimType) && <option value={d.claimRoute}>{CLAIM_ROUTE_LABELS[d.claimRoute]}</option>}
        </select>
        {routeFlash && <p className={HINT} role="status">Route updated to suit the claim type. Change it if needed.</p>}
        <FieldError message={errors.claimRoute} />
      </FormField>
      {claimFieldApplies('track', d) && (
      <FormField variant="info" label="Track">
        <select aria-label="Track" className={SEL(!!errors.track)} value={d.track ?? ''}
          onChange={(e) => set({ track: (e.target.value || null) as ClaimTrack | null })}>
          <option value="">Not set</option>
          {CLAIM_TRACKS.map((t) => <option key={t} value={t}>{CLAIM_TRACK_LABELS[t]}</option>)}
        </select>
        <FieldError message={errors.track} />
      </FormField>
      )}
      {claimFieldApplies('complexityBand', d) && (
      <FormField variant="info" label="Complexity band">
        <select aria-label="Complexity band" className={SEL(!!errors.complexityBand)} value={d.complexityBand ?? ''}
          onChange={(e) => set({ complexityBand: e.target.value ? Number(e.target.value) : null })}>
          <option value="">Not set</option>
          {COMPLEXITY_BANDS.map((b) => <option key={b} value={b}>{complexityBandLabel(b)}</option>)}
        </select>
        <FieldError message={errors.complexityBand} />
      </FormField>
      )}
      {d.claimType === 'CLINICAL_NEGLIGENCE' && (
        <FormField variant="info" label="Fixed recoverable costs">
          <p className="m-0 text-[13.5px] text-casevo-text-soft">{FRC_NOT_YET}</p>
          <p className={HINT}>{FRC_CLIN_NEG_HINT}</p>
        </FormField>
      )}
      {claimFieldApplies('frcRegime', d) && (
      <FormField variant="info" label="Fixed recoverable costs">
        <SegButtons ariaLabel="Fixed recoverable costs" options={[
          { label: 'Yes', checked: d.frcRegime === true, onSelect: () => set({ frcRegime: true }) },
          { label: 'No', checked: d.frcRegime === false, onSelect: () => set({ frcRegime: false }) },
          { label: 'Not confirmed', checked: d.frcRegime === null, onSelect: () => set({ frcRegime: null }) },
        ]} />
        {d.frcRegime === null && suggestFrc !== null && (
          <p className={HINT}>
            Suggested: <b className="font-semibold text-casevo-forest">{suggestFrc ? 'Yes' : 'No'}</b> (accident {suggestFrc ? 'on or after' : 'before'} 1 October 2023).{' '}
            <button type="button" className={`${BTN_SEC} ${SM} !inline-flex align-middle ml-1`} onClick={() => set({ frcRegime: suggestFrc })}>Confirm {suggestFrc ? 'Yes' : 'No'}</button>
          </p>
        )}
        {d.frcRegime === null && d.claimType === 'EL_DISEASE' && <p className={HINT}>{FRC_DISEASE_HINT}</p>}
        <FieldError message={errors.frcRegime} />
      </FormField>
      )}
      <FormField variant="info" label="Date of knowledge">
        {/* allowFuture: a future date reaches the card and is refused with a
            clear message (instead of the widget silently holding it). */}
        <DateInput value={d.dateOfKnowledge} allowFuture ariaLabel="Date of knowledge" fieldClassName={dateCls(!!errors.dateOfKnowledge)}
          onChange={(v) => set({ dateOfKnowledge: v || null })} onValidityChange={typingFor('dateOfKnowledge')} />
        <FieldError message={errors.dateOfKnowledge} />
        {!errors.dateOfKnowledge && warnings.dateOfKnowledge.map((w) => <p key={w} className={WARN} role="status">{w}</p>)}
        <p className={HINT}>A later date of knowledge never moves the estimate. It’s noted on the limitation box.</p>
      </FormField>
      {extra?.holidayRegime && (
        <FormField variant="info" label="Type of holiday claim">
          <select aria-label="Type of holiday claim" className={SEL(!!errors.holidayRegime)} value={d.holidayRegime ?? ''}
            onChange={(e) => set({ holidayRegime: (e.target.value || null) as HolidayRegime | null })}>
            <option value="">Not chosen</option>
            {HOLIDAY_REGIMES.map((r) => <option key={r} value={r}>{HOLIDAY_REGIME_LABELS[r]}</option>)}
          </select>
          <FieldError message={errors.holidayRegime} />
          <p className={HINT}>Sets the limitation rule: package 3 years; air or sea 2 years from arrival; foreign law as entered. Until chosen, 1 year is used.</p>
        </FormField>
      )}
      {extra?.travelArrivalDate && dateField('travelArrivalDate', 'Arrival date', 'When the client arrived (air) or got off the ship (sea).')}
      {extra?.foreignLimitationMonths && (
        <FormField variant="info" label="Foreign limitation period (months)">
          <input type="number" inputMode="numeric" min={FOREIGN_MONTHS_MIN} max={FOREIGN_MONTHS_MAX} step={1}
            aria-label="Foreign limitation period in months" aria-invalid={errors.foreignLimitationMonths ? true : undefined}
            className={inpCls(!!errors.foreignLimitationMonths)} value={d.foreignLimitationMonths ?? ''}
            onChange={(e) => set({ foreignLimitationMonths: e.target.value === '' ? null : Number(e.target.value) })} />
          <FieldError message={errors.foreignLimitationMonths} />
          <p className={HINT}>1 to 120 months. Until entered, 1 year is used.</p>
        </FormField>
      )}
      {extra?.abuseType && (
        <FormField variant="info" label="Type of abuse">
          <select aria-label="Type of abuse" className={SEL(!!errors.abuseType)} value={d.abuseType ?? ''}
            onChange={(e) => set({ abuseType: (e.target.value || null) as AbuseType | null })}>
            <option value="">Not chosen</option>
            {ABUSE_TYPES.map((t) => <option key={t} value={t}>{ABUSE_TYPE_LABELS[t]}</option>)}
          </select>
          <FieldError message={errors.abuseType} />
          {d.abuseType === 'SEXUAL' && !saved.abuseNoLimitConfirmedAt && <p className={HINT}>If the client was under 18, save, then confirm “no time limit” below if it applies.</p>}
          {saved.abuseNoLimitConfirmedAt && d.abuseType !== 'SEXUAL' && <p className={WARN} role="status">Saving this removes the “no time limit” confirmation.</p>}
        </FormField>
      )}
      {extra?.policeFirstReportedAt && dateField('policeFirstReportedAt', 'First reported to police', 'For a child, the CICA time limit runs from this.')}
      {extra?.overseasOperation && (
        <FormField variant="info" label="Overseas operation">
          <SegButtons ariaLabel="Overseas operation" options={[
            { label: 'Yes', checked: d.overseasOperation === true, onSelect: () => set({ overseasOperation: true }) },
            { label: 'No', checked: d.overseasOperation === false, onSelect: () => set({ overseasOperation: false }) },
            { label: 'Not answered', checked: d.overseasOperation === null, onSelect: () => set({ overseasOperation: null }) },
          ]} />
          <FieldError message={errors.overseasOperation} />
          <p className={HINT}>{cap ? `Overseas operations longstop: ${fmtDay(cap.ymd)} (${cap.from} + 6 years). The limitation date can’t be later.` : 'Overseas operations claims have a 6-year longstop.'}</p>
        </FormField>
      )}
      {extra?.diseaseDates && dateField('firstSymptomsDate', 'First symptoms', d.claimType === 'EL_DISEASE' ? 'The estimate runs from the earliest of the accident date, these dates and the date of knowledge.' : 'If one is before the accident date, the estimate runs from it.')}
      {extra?.diseaseDates && dateField('diagnosisDate', 'Diagnosis')}
      {showDateOfDeath(d) && (
        <FormField variant="info" label="Date of death">
          <DateInput value={d.dateOfDeath} allowFuture ariaLabel="Date of death" fieldClassName={dateCls(!!errors.dateOfDeath)}
            onChange={(v) => set({ dateOfDeath: v || null })} onValidityChange={typingFor('dateOfDeath')} />
          <FieldError message={errors.dateOfDeath} />
          {!errors.dateOfDeath && warnings.dateOfDeath.map((w) => <p key={w} className={WARN} role="status">{w}</p>)}
        </FormField>
      )}
      <div className="sm:col-span-2 md:col-span-3">
        <label className="inline-flex items-center gap-[9px] text-[13.5px] text-casevo-forest cursor-pointer select-none">
          <input type="checkbox" className="h-[18px] w-[18px] accent-casevo-brand shrink-0" checked={d.ownOn}
            onChange={(e) => set(e.target.checked
              ? { ownOn: true }
              : { ownOn: false, limitationOverrideDate: null, limitationOverrideReason: '' })} />
          Set limitation date myself
        </label>
        {!d.ownOn && saved.limitationOverrideDate && deadlineYmd && (
          <p className={HINT} role="status">Limitation deadline is still {fmtDay(deadlineYmd)}. Change it in Deadlines if needed.</p>
        )}
        {d.ownOn && (
          <div className={`${FGRID} md:grid-cols-3 mt-[10px] ${REVEALED}`}>
            <FormField variant="info" label="Limitation date" required>
              <DateInput value={d.limitationOverrideDate} allowFuture ariaLabel="Limitation date (set myself)" fieldClassName={dateCls(!!errors.limitationOverrideDate)}
                onChange={(v) => set({ limitationOverrideDate: v || null })} onValidityChange={typingFor('limitationOverrideDate')} />
              <FieldError message={errors.limitationOverrideDate} />
              {!errors.limitationOverrideDate && warnings.limitationOverrideDate.map((w) => <p key={w} className={WARN} role="status">{w}</p>)}
            </FormField>
            <div className="sm:col-span-1 md:col-span-2">
              <FormField variant="info" label="Reason" required>
                <textarea
                  aria-label="Reason for setting the limitation date"
                  aria-required="true"
                  aria-invalid={errors.limitationOverrideReason ? true : undefined}
                  rows={2}
                  maxLength={REASON_MAX}
                  className={`${inpCls(!!errors.limitationOverrideReason)} !h-auto py-[8px] resize-y`}
                  value={d.limitationOverrideReason}
                  placeholder="e.g. Date of knowledge 04/02/2024 (first diagnosis letter)"
                  onChange={(e) => set({ limitationOverrideReason: e.target.value })}
                />
                <FieldError message={errors.limitationOverrideReason} />
                {d.limitationOverrideReason.length >= REASON_MAX - 100 && (
                  <p className={HINT} aria-live="polite">{d.limitationOverrideReason.length} / {REASON_MAX} characters</p>
                )}
              </FormField>
            </div>
            <p className={`${HINT} sm:col-span-2 md:col-span-3 !mt-0`}>Saved as the case’s Limitation deadline, so alerts fire. Changing it in Deadlines later wins.</p>
            {saved.limitationOverrideDate && deadlineYmd && deadlineYmd !== saved.limitationOverrideDate && (
              <p className={`${HINT} sm:col-span-2 md:col-span-3 !mt-0 !text-casevo-warn`} role="status">{ownDateDiffers(saved.limitationOverrideDate, deadlineYmd)}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <section aria-label="Claim" data-focus-card={idPrefix} className={`${CARD} group/card overflow-hidden flex flex-col mt-[14px] transition-[border-color,box-shadow] ${editing ? CARD_EDITING : ''}`}>
      <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
        <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-[#a8e6c5] grid place-items-center shrink-0"><KIcon name="scale" size={17} /></span>
        <div className="min-w-0">
          <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">Claim</h3>
          <div className="text-[12px] text-casevo-text-soft mt-px">Claim type, route, track and limitation inputs</div>
        </div>
        <div className="ml-auto flex gap-1 items-center">
          {editing ? <span className="text-[12.5px] text-casevo-text-soft">Editing</span> : (
            <button type="button" onClick={open} title="Edit Claim" aria-label="Edit Claim"
              className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100">
              {IcEdit(15)}
            </button>
          )}
        </div>
      </div>
      {editing && pending && (
        // (Audit fix batch 2) The shared slim note, with Reload as its link.
        <div className="mx-[18px] mb-3">
          <ActionBox live variant="slim" title={`Updated by ${pending.by}.`} message="Your changes here aren’t saved yet."
            actions={[{ label: 'Reload', kind: 'link', onClick: reload }]} />
        </div>
      )}
      <div className="pt-[2px] px-[18px] pb-4 flex-1 min-w-0">
        {editing ? edit : view}
        {!editing && showAbuseBlock && (
          <AbuseNoLimitBlock caseId={currentCase.id} confirmedAt={saved.abuseNoLimitConfirmedAt} confirmedBy={currentCase.abuseNoLimitConfirmedBy}
            applies={abuseApplies} canConfirm={mayConfirm} />
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
          <span className="mr-auto text-[12px] text-casevo-text-soft flex items-center gap-1.5">
            <i className="w-[7px] h-[7px] rounded-full bg-casevo-mid" aria-hidden="true" />Nothing saves until you press Save · Cancel discards
          </span>
          {draft && clearLine(saved, draft) && <span role="status" className="basis-full text-right text-[12.5px] text-casevo-warn">{clearLine(saved, draft)}</span>}
          {blocked && <span role="alert" className="text-[12px] text-casevo-danger">{FIX_TO_SAVE}</span>}
          {saveError && <span role="alert" className="text-[12px] text-casevo-danger">{saveError}</span>}
          <button type="button" className={`${BTN_SEC} ${SM}`} onClick={cancel} disabled={saving}>Cancel</button>
          <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save() }} disabled={blocked || saving} aria-disabled={blocked || saving}>
            {IcCheck(13)}{saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </section>
  )
}

export default ClaimCard
